import { useCallback, useEffect, useEffectEvent, useRef, useState, type DetailedHTMLProps, type ImgHTMLAttributes } from 'react';
import ExifWorker from 'workers/exif-worker?worker&inline';
import type { Tags } from 'exifreader';
import db from 'utils/datastore/indexed-db';
const exifWorker = new ExifWorker();

// boost me when making breaking changes
const STORE_VERSION = 1;

type ExifSuccess = {
	success: true;
	data: Tags;
	src: string;
}
type ExifFailure = {
	success: false;
	error: string;
	src: string;
}
type ExifMessage = ExifSuccess | ExifFailure;

type Props = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
	fallback?: string;
	fallbackAlt?: string;
};

const logErrors = localStorage.getItem('LOG_IMAGE_ERRORS');

const getDescriptionFromExifMessage = (exif: ExifSuccess) => {
	// XML description can contain the most data - ImageDescription is default but it's clamped to a short length
	// so we will start with the XML description if it exists
	if (exif.data.description?.description) {
		return exif.data.description.description;
	}

	// publishing company standard
	if (exif.data['Caption/Abstract']?.description) { 
		return exif.data['Caption/Abstract'].description;
	}

	// exif description follows after
	if (exif.data['ImageDescription']?.description) {
		return exif.data['ImageDescription'].description;
	}

	// then XML About
	if (exif.data.about?.description) {
		return exif.data.about.description;
	}

	// then XML title
	if (exif.data.title?.description) {
		return exif.data.title?.description;
	}

	// finally, windows default
	if (exif.data['XP Title']?.description) {
		return exif.data['XP Title'].description;
	}
};

const Image = (props: Props) => {
	const { fallbackAlt, ...rest } = props;
	const [src, setSrc] = useState(props.src);
	const [alt, setAlt] = useState(props.alt);
	const exifLoadingRef = useRef(false);
	const idb = useRef(db);

	const handleExif = useEffectEvent((e: MessageEvent<ExifMessage>) => {
		if (e.data.src !== props.src) return;

		if (e.data.success) {
			// load the image's exif data description THEN about THEN title in order of precedence 
			setAlt(getDescriptionFromExifMessage(e.data) ?? fallbackAlt);

			// when making changes update this version number to drop the previous cache
			idb.current.putItem({ uuid: e.data.src, type: 'img-meta' + STORE_VERSION, data: e.data }, { days: 90 }).catch(e => { 
				console.error(e);
			});
		} else { 
			if (logErrors) { 
				console.error(e.data.error);
			}
			setAlt(fallbackAlt);
		}

		exifLoadingRef.current = false;
	});

	useEffect(() => {
		// if an alt is set already, do nothing.
		if (alt || !props.src) return;

		// if it's an svg with a fallback alt, just use the fallback alt as-is
		if (props.src.endsWith('.svg')) { 
			setAlt(fallbackAlt);
			return;
		}

		const src = props.src;
		exifWorker.addEventListener('message', handleExif);
		if (!exifLoadingRef.current) { 
			exifLoadingRef.current = true;
			idb.current.getItem<ExifSuccess>('img-meta' + STORE_VERSION, src).then(value => {
				if (value) {
					setAlt(getDescriptionFromExifMessage(value) ?? fallbackAlt);
					return;
				}

				fetch(src, {
					mode: 'cors',
					headers: {
						origin: window.location.origin
					},
					cache: 'no-cache' // block cache because of CORS
				}).then(res => {
					if (res.ok) {
						return res.arrayBuffer();
					} else { 
						if (logErrors) console.error(res.headers, res.status);
					}
				}).then(buffer => {
					if (buffer) {
						exifWorker.postMessage({ src, buffer }, [buffer]);
					} else { 
						exifLoadingRef.current = false;
					}
				}).catch(e => { 
					if (logErrors) { 
						console.error(e);
					}
					exifLoadingRef.current = false;
				});
			});
		}
		return () => {
			exifWorker.removeEventListener('message', handleExif);
		};
	}, [props.src, alt, fallbackAlt]);

  const onError = useCallback(() => {
    setSrc(props.fallback);
	}, [props.fallback]);
	
	return <img {...rest} alt={ alt} src={src} onError={onError} />;
};

export default Image;
